import { DepartureCityId } from '@/entities/departureCity';
import type { SearchCriteria } from '@/entities/searchCriteria';
import type { DepartureCity } from '@/shared/model';
import FlexibleDatesState from './FlexibleDatesState';
import type { AlternativeSearchContext, AlternativeSearchState, AlternativeSearchView } from './types';
import { getAlternativeDepartureCities, withDepartureCity } from '../../lib/alternativeSearchCriteria';
import { hasSerpProducts } from '../../lib/toSerpProducts';

function findMoscow({ formData }: AlternativeSearchContext): DepartureCity | null {
  return formData?.cities.find(({ id }) => id === DepartureCityId.Moscow) ?? null;
}

export default class DepartureCityState implements AlternativeSearchState {
  readonly criteria: SearchCriteria | null;

  private readonly unsubscribe: () => void;

  constructor(
    private readonly context: AlternativeSearchContext,
    private readonly city: DepartureCity | null = findMoscow(context),
    private readonly emptyCityIds: readonly number[] = [],
  ) {
    const { fromCity } = context.userCriteria;

    this.criteria = fromCity && city && fromCity.id !== city.id ? withDepartureCity(context.userCriteria, city) : null;

    this.unsubscribe = context.productSearch.on('end', (result) => {
      if (result.state !== 'finished' || hasSerpProducts(result)) return;

      if (!city || city.id === DepartureCityId.Moscow) {
        context.setState(new FlexibleDatesState(context));
        return;
      }

      context.setState(new DepartureCityState(context, findMoscow(context), [...emptyCityIds, city.id]));
    });
  }

  get view(): AlternativeSearchView | null {
    const { city, emptyCityIds, context } = this;
    const userCity = context.userCriteria.fromCity;
    if (!city || !userCity) return null;

    const cities = getAlternativeDepartureCities(context.formData?.cities ?? [], {
      userCityId: userCity.id,
      currentCityId: city.id,
      emptyCityIds,
    });

    return { kind: 'departureCity', userCity, city, cities };
  }

  start(): void {
    const { criteria, context } = this;

    if (!criteria) {
      context.setState(new FlexibleDatesState(context));
      return;
    }

    context.productSearch.run(criteria);
  }

  dispose(): void {
    this.unsubscribe();
  }

  selectCity(city: DepartureCity): void {
    this.context.setState(new DepartureCityState(this.context, city, this.emptyCityIds));
  }
}
